import { useState } from 'react';
import { Button, Label, TextInput } from 'flowbite-react';
import { Mail, Phone, User, Lock, UserPlus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import { userApi } from '../../api/user';
import { useAuth } from '../../context/AuthContext';

const INITIAL_FORM = {
  name: '',
  email: '',
  password: '',
  mobileNumber: '',
};

export default function CreateFinanceOfficer() {
  const { tokens } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.name.trim()) {
      nextErrors.name = 'Name is required';
    }

    if (!form.email.trim()) {
      nextErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = 'Enter a valid email address';
    }

    if (!form.password) {
      nextErrors.password = 'Password is required';
    } else if (form.password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters';
    } else if (
      !/\d/.test(form.password) ||
      !/[a-zA-Z]/.test(form.password)
    ) {
      nextErrors.password = 'Password must contain at least one letter and one number';
    }

    if (!form.mobileNumber.trim()) {
      nextErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^0\d{10}$/.test(form.mobileNumber.trim())) {
      nextErrors.mobileNumber = 'Enter a valid 11-digit mobile number (e.g. 09171234567)';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        mobileNumber: form.mobileNumber.trim(),
      };

      const { status, data } = await userApi.createFinanceOfficer(
        tokens.access.token,
        payload
      );

      if (status >= 200 && status < 300) {
        toast.success('Finance officer created successfully');
        setForm(INITIAL_FORM);
        setErrors({});
      } else {
        toast.error(data?.message || 'Failed to create finance officer');
      }
    } catch (err) {
      console.error('Failed to create finance officer:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className='bg-[#F8F9FB] min-h-screen pb-24'>
      <HeaderWithIcon title='Add Finance Officer' />

      <div className='px-4 py-3'>
        <div className='bg-white rounded-lg shadow-sm p-5'>
          <div className='mb-5 flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600'>
              <UserPlus className='h-5 w-5' />
            </div>
            <div>
              <h2 className='text-base font-bold text-gray-800'>
                New Finance Officer
              </h2>
              <p className='text-xs text-gray-500'>
                Create a finance officer account.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className='space-y-4' noValidate>
            <div>
              <div className='mb-1 block'>
                <Label htmlFor='name' value='Full Name' />
              </div>
              <TextInput
                id='name'
                name='name'
                type='text'
                icon={User}
                placeholder='Jane Finance'
                value={form.name}
                onChange={handleChange}
                color={errors.name ? 'failure' : 'gray'}
                helperText={errors.name}
              />
            </div>

            <div>
              <div className='mb-1 block'>
                <Label htmlFor='email' value='Email' />
              </div>
              <TextInput
                id='email'
                name='email'
                type='email'
                icon={Mail}
                placeholder='finance@example.com'
                value={form.email}
                onChange={handleChange}
                color={errors.email ? 'failure' : 'gray'}
                helperText={errors.email}
              />
            </div>

            <div>
              <div className='mb-1 block'>
                <Label htmlFor='mobileNumber' value='Mobile Number' />
              </div>
              <TextInput
                id='mobileNumber'
                name='mobileNumber'
                type='tel'
                icon={Phone}
                placeholder='09171234567'
                value={form.mobileNumber}
                onChange={handleChange}
                color={errors.mobileNumber ? 'failure' : 'gray'}
                helperText={errors.mobileNumber}
              />
            </div>

            <div>
              <div className='mb-1 block'>
                <Label htmlFor='password' value='Password' />
              </div>
              <TextInput
                id='password'
                name='password'
                type={showPassword ? 'text' : 'password'}
                icon={Lock}
                placeholder='At least 8 characters'
                value={form.password}
                onChange={handleChange}
                color={errors.password ? 'failure' : 'gray'}
                helperText={errors.password}
              />
              <button
                type='button'
                onClick={() => setShowPassword((prev) => !prev)}
                className='mt-1 text-xs font-medium text-blue-600'
              >
                {showPassword ? 'Hide password' : 'Show password'}
              </button>
            </div>

            <div className='flex gap-2 pt-2'>
              <Button
                type='button'
                color='gray'
                className='flex-1'
                onClick={() => navigate(-1)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                color='blue'
                className='flex-1'
                disabled={submitting}
              >
                {submitting ? 'Creating...' : 'Create Officer'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
